import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass } from '../../utils/fontSizeUtils';
import { StockTableLayout, Statistic } from '../../components/StockTable';
import { syncApiService } from '../../services/syncApi';
import { TickerDisplayData } from '../../types/ticker';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import { userTickerListApi, UserTickerList, UserTickerListWithTickers } from '../../services/userTickerListApi';
import { userApi } from '../../services/userApi';
import { usageLimitsApi } from '../../services/usageLimitsApi';
import CategoryInfoModal from '../../components/CategoryInfoModal';
import IndicatorInfoModal from '../../components/IndicatorInfoModal/IndicatorInfoModal';
import TabBar from '../../components/Common/TabBar';
import DeleteListConfirmationModal from '../../components/Modals/DeleteListConfirmationModal';
import ErrorDialog from '../../components/Modals/ErrorDialog';
import ShareListModal from '../../components/ShareListModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { 
  cacheUserLists, 
  getCachedUserLists, 
  clearListCache,
  clearUserListsCache,
  setCacheInvalidation,
  cacheCurrentListName,
  getCachedCurrentListName,
  clearCurrentListNameCache,
  cacheCurrentListId,
  getCachedCurrentListId,
  clearCurrentListIdCache,
  generateUniqueListName
} from '../../utils/listCache';
import { sessionSaveService } from '../../services/sessionSaveService';
import { useEditMode } from '../../hooks/useEditMode';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useModals } from '../../hooks/useModals';
import { useUserListManagement } from '../../hooks/useUserListManagement';
import { useListPreferences } from '../../hooks/useListPreferences';
import { useAddTicker } from '../../hooks/useAddTicker';
import { publicListApi, PublicListTickerMapping, PublicListPreferences } from '../../services/publicListApi';
import {
  FUNDAMENTAL_STATISTICS,
  ANNUAL_FUNDAMENTAL_STATISTICS,
  FUNDAMENTAL_CATEGORY_HEADERS,
  ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS,
} from '../../utils/fundamentalsTabUtils';
import {
  formatFundamentalsStatisticValue,
  getFundamentalsStatisticColor,
} from '../../hooks/useFundamentalsTabStatistics';
import { LockIcon } from '../../components/StockTable/StockTable';

// Default tickers to display
const DEFAULT_TICKERS = ['NVDA', 'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'META', 'TSLA'];

interface RatioData {
  tickerBasicsId: number;
  ratioKey: string;
  ratioValue: number;
  ratioCategory: string | null;
  timeframe: string;
  year: number;
  quarter: number;
  periodEndDate: Date;
}

interface FinancialData {
  tickerBasicsId: number;
  statsKey: string;
  statsValue: number;
  statsUnit: string;
  category: string;
  timeframe: string;
  year: number;
  quarter: number;
  periodEndDate: Date;
}

interface PriceData {
  ticker_symbol: string;
  ticker_basics_id: number;
  close_price_adjusted: number;
  date: Date;
}

const ListFundamentals: React.FC = () => {
  // Set page title
  useEffect(() => {
    document.title = 'Stock Table - Fundamentals';
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Pro-only statistics (always show PRO tag, regardless of subscription status)
  const PRO_ONLY_STATISTICS = [
    'ev_fcf_ratio',
    'cash_flow_margin',
    'ocf_ratio',
    'fcf_yield',
    'roic',
  ];
  
  // Check for public_uuid and timeframe query parameters
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const timeframeParam = searchParams.get('timeframe');
  const isPublicList = !!publicUuid;
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    publicListName: string | null;
    tickerMappings: PublicListTickerMapping[];
    listPreferences: PublicListPreferences | null;
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  // Determine locked statistics based on pro subscription status (for actually locking values)
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  const [isLoadingPublicList, setIsLoadingPublicList] = useState(false);
  const [publicListError, setPublicListError] = useState<string | null>(null);
  const [isPublicListNotFound, setIsPublicListNotFound] = useState(false);
  
  const [tickerList, setTickerList] = useState<string[]>(() => {
    // For public lists, start with empty list - will be populated from public list data
    if (isPublicList) {
      return [];
    }
    // For authenticated users, start with empty list to avoid default ticker API calls
    if (isAuthenticated) {
      return [];
    }
    // For unauthenticated users, only use session storage if it exists, otherwise start empty
    // Default tickers should only be shown on the Landing page
    const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
    return savedTickers ? JSON.parse(savedTickers) : [];
  });
  
  
  const [isEditingTickers, setIsEditingTickers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tickerData, setTickerData] = useState<{ [symbol: string]: TickerDisplayData }>({});
  const [tickerNames, setTickerNames] = useState<{ [symbol: string]: string }>({});
  const [tickerSymbols, setTickerSymbols] = useState<{ [symbol: string]: string }>({});
  
  const [listName, setListName] = useState<string>(() => {
    // For public lists, check sessionStorage for cached public list name
    if (isPublicList && publicUuid) {
      const cachedPublicListName = sessionStorage.getItem(`public-list-name-${publicUuid}`);
      if (cachedPublicListName) {
        return cachedPublicListName;
      }
      // Return empty string if no cached name, will be set when data loads
      return '';
    }
    
    // For authenticated users, try to get cached list name first
    if (isAuthenticated) {
      const cachedListName = getCachedCurrentListName();
      if (cachedListName) {
        return cachedListName;
      }
    }
    
    // Fallback to session storage or default
    const savedListName = sessionStorage.getItem('stocktable-list-name');
    return savedListName || 'Main List';
  });
  
  const [isSyncing, setIsSyncing] = useState(false);
  // Initialize syncingTickers from sessionStorage to restore state after refresh
  // Note: currentListId is not available yet at initialization, so we use global key initially
  // The helper function will use list ID-specific key when actually reading/writing
  const [syncingTickers, setSyncingTickers] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('stocktable-syncing-tickers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  // Initialize timeframe from URL parameter if available
  const [timeframe, setTimeframe] = useState<'quarterly' | 'annual'>(() => {
    // Use timeframe from URL parameter if available, otherwise default to 'quarterly'
    if (timeframeParam === 'quarterly' || timeframeParam === 'annual') {
      return timeframeParam;
    }
    return 'quarterly';
  });
  
  // Update timeframe when URL parameter changes
  useEffect(() => {
    if (timeframeParam === 'quarterly' || timeframeParam === 'annual') {
      setTimeframe(timeframeParam);
    }
  }, [timeframeParam]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isIndicatorModalOpen, setIsIndicatorModalOpen] = useState(false);
  const [selectedIndicatorKey, setSelectedIndicatorKey] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Sync status management
  const [syncedTickers, setSyncedTickers] = useState<Set<string>>(new Set());
  const [unsyncedTickers, setUnsyncedTickers] = useState<Set<string>>(new Set());
  const [pollingLimitReached, setPollingLimitReached] = useState<Set<string>>(new Set());
  
  // Ref to track if we've restored syncing state on initial load
  const hasRestoredSyncingStateRef = useRef(false);
  // Ref to prevent recoverPendingTickers from running multiple times
  const hasRecoveredPendingTickersRef = useRef(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState(0);
  const [isAddingNewTicker, setIsAddingNewTicker] = useState(false);
  
  // Refs for polling management to prevent stale closures and race conditions
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for synchronous access
  const isCheckingSyncRef = useRef(false); // Flag to prevent concurrent sync checks
  const currentAttemptRef = useRef(1); // Track current attempt number for interval callback
  
  // Refs to track when we need to reload data
  const refreshUsageLimitsRef = useRef<(() => void) | null>(null);
  
  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Store original session storage values to restore when leaving public list
  const originalSessionStorageRef = useRef<{
    tickerList: string | null;
    listName: string | null;
    hiddenStatistics: string | null;
  } | null>(null);

  // Track if we've attempted to fetch the public list (to prevent infinite retries)
  const publicListFetchAttemptedRef = useRef<string | null>(null);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    // Only fetch if we haven't already attempted for this UUID, or if it's a different UUID
    const shouldFetch = publicUuid && 
                       !publicListData && 
                       !isLoadingPublicList && 
                       publicListFetchAttemptedRef.current !== publicUuid;
    
    if (shouldFetch) {
      // Save original session storage values before loading public list (only for unauthenticated users)
      if (!isAuthenticated && !originalSessionStorageRef.current) {
        originalSessionStorageRef.current = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list'),
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics'),
        };
      }

      // Mark that we're attempting to fetch this UUID
      publicListFetchAttemptedRef.current = publicUuid;
      setIsLoadingPublicList(true);
      setPublicListError(null);
      
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            publicListName: response.data.public_list_name,
            tickerMappings: response.data.ticker_mappings,
            listPreferences: response.data.list_preferences,
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
          
          // Update ticker list from mappings
          const tickers = response.data.ticker_mappings
            .sort((a, b) => a.order_num - b.order_num)
            .map(mapping => mapping.ticker_symbol);
          setTickerList(tickers);
          
          // Update list name and cache it for tab switching
          const publicListName = response.data.public_list_name || 'Public List';
          setListName(publicListName);
          // Cache the public list name in sessionStorage for immediate restoration on tab switch
          if (publicUuid) {
            sessionStorage.setItem(`public-list-name-${publicUuid}`, publicListName);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          const errorMessage = error.message || 'Failed to load public list';
          setPublicListError(errorMessage);
          
          // For 404 or 403 errors, set flag to show not found/not shared message instead of table
          if ((error as any).status === 404 || errorMessage.includes('not found')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list does not exist or is no longer available.');
          } else if ((error as any).status === 403 || errorMessage.includes('not publicly shared')) {
            setIsPublicListNotFound(true);
            setTickerList([]);
            setListName('Public List');
            setPublicListError('This list is not publicly shared.');
          }
        })
        .finally(() => {
          setIsLoadingPublicList(false);
        });
    }
    
    // Reset the attempted ref and not found flag when publicUuid changes or becomes null
    if (!publicUuid) {
      publicListFetchAttemptedRef.current = null;
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, publicListData, isLoadingPublicList, isAuthenticated]);

  // Restore session storage on component unmount if we were viewing a public list
  useEffect(() => {
    return () => {
      // Cleanup: restore original session storage when component unmounts if we were in public list mode
      if (originalSessionStorageRef.current && !isAuthenticated) {
        const original = originalSessionStorageRef.current;
        // Restore synchronously before component unmounts
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        originalSessionStorageRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Reset not found flag and clear error when publicUuid becomes null (e.g., when clicking logo)
  useEffect(() => {
    if (!publicUuid) {
      setIsPublicListNotFound(false);
      setPublicListError(null);
      setPublicListData(null);
    }
  }, [publicUuid]);

  // Also restore when location changes away from public list (before unmount)
  // Watch location.search directly to catch URL changes even when component doesn't unmount
  useEffect(() => {
    // If we're no longer in public list mode, restore from session storage
    if (!publicUuid && !isAuthenticated) {
      // If we have stored original values, restore them
      if (originalSessionStorageRef.current) {
        const original = originalSessionStorageRef.current;
        
        // Restore session storage
        if (original.tickerList !== null) {
          sessionStorage.setItem('stocktable-ticker-list', original.tickerList);
        } else {
          sessionStorage.removeItem('stocktable-ticker-list');
        }
        if (original.listName !== null) {
          sessionStorage.setItem('stocktable-list-name', original.listName);
        } else {
          sessionStorage.removeItem('stocktable-list-name');
        }
        if (original.hiddenStatistics !== null) {
          sessionStorage.setItem('stocktable-hidden-statistics', original.hiddenStatistics);
        } else {
          sessionStorage.removeItem('stocktable-hidden-statistics');
        }
        
        // Reset component state to match restored session storage
        if (original.tickerList !== null) {
          try {
            const restoredTickers = JSON.parse(original.tickerList);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no original data (default tickers only on Landing page)
          setTickerList([]);
        }
        
        if (original.listName !== null) {
          setListName(original.listName);
        } else {
          const savedListName = sessionStorage.getItem('stocktable-list-name');
          setListName(savedListName || 'Main List');
        }
        
        originalSessionStorageRef.current = null;
      } else {
        // If we don't have original values saved, restore from current session storage
        const savedTickers = sessionStorage.getItem('stocktable-ticker-list');
        if (savedTickers) {
          try {
            const restoredTickers = JSON.parse(savedTickers);
            setTickerList(restoredTickers);
          } catch (e) {
            // If parsing fails, start with empty list (default tickers only on Landing page)
            setTickerList([]);
          }
        } else {
          // Start with empty list if no session storage (default tickers only on Landing page)
          setTickerList([]);
        }
        
        const savedListName = sessionStorage.getItem('stocktable-list-name');
        setListName(savedListName || 'Main List');
      }
      
      // Clear public list data
      setPublicListData(null);
      setPublicListError(null);
      setIsPublicListNotFound(false);
    }
  }, [publicUuid, location.search, isAuthenticated]);

  // User list management hook (only for non-public lists)
  const {
    userLists,
    currentListId,
    isLoadingUserLists,
    setUserLists,
    setCurrentListId,
    needsUserListsReload,
    needsListDataReload,
    handleListChange,
    handleCreateNewList,
    handleCreateNewListWithName,
  } = useUserListManagement({
    isAuthenticated: isAuthenticated && !isPublicList,
    tickerList,
    listName,
    setTickerList,
    setListName,
    refreshUsageLimitsRef,
    returnPath: '/fundamentals',
  });

  // List preferences hook (only for non-public lists)
  const {
    hiddenStatistics: privateHiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  } = useListPreferences({
    isAuthenticated: isAuthenticated && !isPublicList,
    currentListId: isPublicList ? null : currentListId,
    pageType: 'fundamentals',
    persistToSessionStorageImmediately: true, // Match previous behavior
  });

  // Determine which lists are locked (beyond 3rd position for free users)
  // Free users can only access the first 3 lists (sorted alphabetically)
  const sortedUserLists = useMemo(() => {
    return [...userLists].sort((a, b) => {
      const nameA = a.list_name.toLowerCase();
      const nameB = b.list_name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [userLists]);

  const lockedListIds = useMemo(() => {
    if (isAuthenticated && !user?.hasProSubscription && sortedUserLists.length > 3) {
      // Lists beyond the 3rd position (index 3+) are locked
      return sortedUserLists.slice(3).map(list => list.id);
    }
    return [];
  }, [sortedUserLists, isAuthenticated, user?.hasProSubscription]);

  // Check if current list is locked
  const isCurrentListLocked = useMemo(() => {
    return currentListId !== null && lockedListIds.includes(currentListId);
  }, [currentListId, lockedListIds]);

  // Use public list preferences if in public list mode, otherwise use private preferences
  const hiddenStatistics = isPublicList && publicListData?.listPreferences?.fundamental_indicators_to_hide
    ? publicListData.listPreferences.fundamental_indicators_to_hide
    : privateHiddenStatistics;

  // Auto-save function for authenticated users (needed for handleSaveChanges)
  const autoSaveList = useCallback(async (tickers: string[]) => {
    // Don't auto-save in public list mode
    if (isPublicList || !isAuthenticated) return;
    
    // Determine which list ID to use
    let listId = currentListId;
    
    // If currentListId is null, try to get it from userLists or wait for it
    if (listId === null) {
      // First, try to get from cache (might be set by createDefaultMainListIfNeeded)
      const { getCachedCurrentListId } = await import('../../utils/listCache');
      const cachedListId = getCachedCurrentListId();
      if (cachedListId) {
        console.log('💾 Using cached list ID for auto-save:', cachedListId);
        listId = cachedListId;
      } else if (userLists.length > 0) {
        // If userLists has items, use the first list (or try to get default list)
        // Try to get the user's default list
        try {
          const defaultListResponse = await userApi.getDefaultList();
          if (defaultListResponse.defaultUserTickerListId) {
            const defaultListExists = userLists.some(list => list.id === defaultListResponse.defaultUserTickerListId);
            if (defaultListExists) {
              listId = defaultListResponse.defaultUserTickerListId;
            } else {
              // Default list doesn't exist in userLists, use first list
              listId = userLists[0].id;
            }
          } else {
            // No default list set, use first list
            listId = userLists[0].id;
          }
        } catch (error) {
          // If we can't get default list, use first list
          console.log('⚠️ Could not get default list, using first list:', error);
          listId = userLists[0].id;
        }
      } else {
        // No lists available yet - ticker is saved to sessionStorage and will be recovered when lists load
        console.log('⚠️ Auto-save skipped: currentListId is null and no userLists available. Ticker saved to sessionStorage.');
        return;
      }
    }
    
    // Ensure listId is not null before proceeding
    if (!listId) {
      console.log('⚠️ Auto-save skipped: No list ID available');
      return;
    }
    
    try {
      console.log('💾 Auto-saving list for authenticated user:', { listId, tickers });
      const tickerMappings = tickers.map((ticker, index) => ({
        ticker_symbol: ticker,
        order_num: index + 1
      }));
      
      await userTickerListApi.updateTickerMappings(listId, tickerMappings);
      console.log('✅ Auto-save successful');
      
      // Clear the cache for this list to force reload on tab switch
      clearListCache(listId);
      console.log('🗑️ Cleared cache for list', listId);
      
      // Mark that we need to reload list data to ensure fresh data is loaded
      needsListDataReload.current = true;
      console.log('🔄 Marked list data for reload');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  }, [isAuthenticated, currentListId, isPublicList, userLists]);

  // Helper function to get sessionStorage key based on list ID
  const getTickerListKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-ticker-list-${currentListId}` 
      : 'stocktable-ticker-list';
  }, [isAuthenticated, currentListId]);

  // Helper function to get syncing tickers sessionStorage key based on list ID
  const getSyncingTickersKey = useCallback(() => {
    return isAuthenticated && currentListId !== null 
      ? `stocktable-syncing-tickers-${currentListId}` 
      : 'stocktable-syncing-tickers';
  }, [isAuthenticated, currentListId]);

  // Handle save changes (used by edit mode hook)
  const handleSaveChanges = useCallback(async (saveTempTickerList: string[], saveTempListName: string) => {
    // Don't allow saving changes in public list mode
    if (isPublicList) {
      return;
    }
    // Deduplicate the list before saving
    const newTickerList = Array.from(new Set([...saveTempTickerList]));

    if (!isAuthenticated) {
      sessionStorage.setItem(getTickerListKey(), JSON.stringify(newTickerList));
    }

    setTickerList(newTickerList);
    setListName(saveTempListName);
    setIsEditingTickers(false);
    
    // Auto-save for authenticated users
    if (isAuthenticated && currentListId) {
      try {
        // Update list name if it changed
        if (saveTempListName !== listName) {
          console.log('📝 Updating list name:', { listId: currentListId, oldName: listName, newName: saveTempListName });
          await userTickerListApi.updateListName(currentListId, saveTempListName);
          console.log('✅ List name updated successfully');
          
          // Update the user lists cache to reflect the name change
          setUserLists((prev: UserTickerList[]) => prev.map((list: UserTickerList) => 
            list.id === currentListId 
              ? { ...list, list_name: saveTempListName }
              : list
          ));
          
          // Cache the updated list name to prevent flickering on tab switch
          cacheCurrentListName(saveTempListName);
          console.log('🗑️ Cached updated list name to prevent flickering');
        }
        
        // Update ticker mappings
        await autoSaveList(newTickerList);
        
        // Refresh usage limits after successful mappings update
        try {
          await Promise.all([
            usageLimitsApi.checkTickerLimit(),
            usageLimitsApi.checkListLimit()
          ]);
          console.log('✅ Usage limits refreshed after mappings update');
        } catch (error) {
          console.error('Failed to refresh usage limits:', error);
        }
        
        // Save user list preferences for fundamental indicators
        try {
          await savePreferences(hiddenStatistics);
        } catch (error) {
          console.error('❌ Failed to save fundamental preferences:', error);
          // Don't throw error to prevent UI from breaking
        }
      } catch (error) {
        console.error('❌ Failed to update list:', error);
      }
    } else {
      // Save list name to session storage for unauthenticated users
      sessionStorage.setItem('stocktable-list-name', saveTempListName);
      // Preferences are already saved via savePreferences in the hook
      await savePreferences(hiddenStatistics);
    }
    
    // Note: sessionStorage is already updated above before state changes
    // This prevents the recovery mechanism from restoring removed tickers
  }, [tickerList, listName, isAuthenticated, currentListId, setTickerList, setListName, setIsEditingTickers, hiddenStatistics, setUserLists, autoSaveList, savePreferences]);

  // Edit mode hook
  const {
    tempTickerList,
    tempListName,
    setTempTickerList,
    setTempListName,
    handleStartEdit,
    handleCancelEdit,
    handleDoneEdit,
    handleSave,
  } = useEditMode({
    tickerList,
    listName,
    setTickerList,
    setListName,
    isEditingTickers,
    setIsEditingTickers,
    returnPath: '/fundamentals',
    onSaveChanges: handleSaveChanges,
    hiddenStatistics: !isPublicList ? { fundamentals: privateHiddenStatistics } : undefined,
  });

  // Drag and drop hook (uses 'insert' behavior by default)
  const {
    draggedTicker,
    dragOverTicker,
    droppedTicker,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
  });


  // Function to clear interval without setting stopped flag (for cleanup/reset)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Helper function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
  };

  // Centralized function to handle 429 errors
  const handleRateLimitError = () => {
    console.log('Rate limit (429) detected, stopping all polling and API calls immediately');
    setError(`Rate limit reached. Please wait before trying again.`);
    
    // Stop all polling
    stopPolling();
    
    // Clear all sync states
    setSyncingTickers(new Set());
    setUnsyncedTickers(new Set());
    setPollingLimitReached(new Set());
  };

  // Determine which ticker list to use for display
  const displayTickerList = isEditingTickers ? tempTickerList : tickerList;

  // Determine locked tickers (beyond 10th position for free users OR all tickers if list is locked)
  // Free users can only access the first 10 tickers in a list
  // If the list itself is locked, all tickers are locked
  // For public lists, use owner's subscription status instead of viewer's
  // Note: Calculate after tempTickerList is available from useEditMode hook
  const lockedTickers = useMemo(() => {
    if (isCurrentListLocked) {
      // If list is locked, lock all tickers
      return displayTickerList;
    }
    // For public lists, use owner's subscription status
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription 
        ? [] // Owner has PRO, so no locked tickers
        : displayTickerList.slice(10); // Owner doesn't have PRO, apply same locks
    }
    // For private lists, use viewer's subscription status
    return (!isAuthenticated || !user?.hasProSubscription) 
      ? displayTickerList.slice(10) // Tickers beyond the 10th position (index 10+)
      : [];
  }, [displayTickerList, isAuthenticated, user?.hasProSubscription, isCurrentListLocked, isPublicList, publicListData]);

  // Persist ticker list (only for non-authenticated users)
  // Always deduplicate before saving to prevent duplicates
  useEffect(() => {
    if (!isAuthenticated && !isPublicList) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(uniqueTickerList));
    }
  }, [tickerList, isAuthenticated, isPublicList]);
  
  // Ensure tickerList is always deduplicated (for authenticated users too)
  useEffect(() => {
    if (tickerList.length > 0) {
      const uniqueTickerList = Array.from(new Set(tickerList));
      if (uniqueTickerList.length !== tickerList.length) {
        console.log(`🔧 ListFundamentals: Deduplicating tickerList: ${tickerList.length} -> ${uniqueTickerList.length}`);
        setTickerList(uniqueTickerList);
      }
    }
  }, [tickerList]);




  // Function to check sync status and fetch data for specific tickers
  const checkSyncAndFetchDataForTickers = async (tickersToCheck: string[], attemptNumber: number = 1) => {
    console.log('ListFundamentals: checkSyncAndFetchDataForTickers called with:', tickersToCheck, `(attempt ${attemptNumber})`);
    
    if (tickersToCheck.length === 0) {
      console.log('ListFundamentals: No tickers to check, returning early');
      return;
    }

    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 5) {
      console.warn(`ListFundamentals: Attempt ${attemptNumber} exceeds limit (5), stopping`);
      stopPolling();
      setPollingLimitReached(prev => {
        const newSet = new Set(prev);
        tickersToCheck.forEach((ticker: string) => newSet.add(ticker));
        return newSet;
      });
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Don't proceed if polling has been stopped (e.g., after reaching limit or 429 error)
    if (pollingStoppedRef.current) {
      console.log(`ListFundamentals: checkSyncAndFetchDataForTickers called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }

    // Guard: Prevent concurrent execution - if a sync check is already in progress, ignore this call
    if (isCheckingSyncRef.current) {
      console.log(`ListFundamentals: checkSyncAndFetchDataForTickers already in progress, ignoring concurrent call`);
      return;
    }

    isCheckingSyncRef.current = true;

    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);

    try {
      console.log('🚀 ListFundamentals: About to call checkTickerSyncStatus for:', tickersToCheck);
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: tickersToCheck,
      });
      console.log('🚀 ListFundamentals: API call completed successfully for:', tickersToCheck);
      console.log('🚀 ListFundamentals: API response:', syncResponse);

      // Calculate last updated from latest last_sync_ended_at of synced tickers
      if (syncResponse.success && syncResponse.data && syncResponse.data.tickers) {
        const syncedTickers = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickers.length > 0) {
          const lastSyncDates = syncedTickers
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
      }

      // Store ticker names and symbols from sync response (same pattern as other components)
      const newTickerNames: { [symbol: string]: string } = {};
      const newTickerSymbols: { [symbol: string]: string } = {};
      syncResponse.data.tickers.forEach((ticker: any) => {
        if (ticker.ticker_name) {
          newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
          // Cache in sessionStorage like other components
          sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
        }
        if (ticker.ticker_symbol) {
          newTickerSymbols[ticker.ticker_symbol] = ticker.ticker_symbol;
          // Cache in sessionStorage like other components
          sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
        }
      });
      setTickerNames(prev => ({ ...prev, ...newTickerNames }));
      setTickerSymbols(prev => ({ ...prev, ...newTickerSymbols }));

      const newlySyncedTickers = syncResponse.data.tickers
        .filter((ticker: any) => ticker.is_synced === true)
        .map((ticker: any) => ticker.ticker_symbol);
      
      const stillUnsyncedTickers = syncResponse.data.tickers
        .filter((ticker: any) => ticker.is_synced === false)
        .map((ticker: any) => ticker.ticker_symbol);

      console.log('🚀 ListFundamentals: newlySyncedTickers:', newlySyncedTickers);
      console.log('🚀 ListFundamentals: stillUnsyncedTickers:', stillUnsyncedTickers);

      // Update sync status sets
      setSyncedTickers(prev => {
        const newSet = new Set(prev);
        newlySyncedTickers.forEach((ticker: string) => newSet.add(ticker));
        return newSet;
      });
      
      setUnsyncedTickers(prev => {
        const newSet = new Set(prev);
        stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
        // Remove newly synced tickers from unsynced set
        newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
        return newSet;
      });

      // Remove newly synced tickers from syncing set so they don't show loading animation
      if (newlySyncedTickers.length > 0) {
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          newlySyncedTickers.forEach((ticker: string) => newSet.delete(ticker));
          return newSet;
        });
      }

      // Fetch data for newly synced tickers using optimized batched approach
      if (newlySyncedTickers.length > 0) {
        console.log(`ListFundamentals: Fetching data for newly synced tickers: ${newlySyncedTickers.join(', ')}`);
        
        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Fetch fundamental data for newly synced tickers using optimized batched approach
        await fetchFundamentalDataForTickersOptimized(newlySyncedTickers, syncResponse.data);
        // Safety: if data is fetched successfully, ensure ticker no longer shows syncing/unsynced pulse.
        setSyncingTickers(prev => {
          const next = new Set(prev);
          newlySyncedTickers.forEach((t: string) => next.delete(t));
          return next;
        });
        setUnsyncedTickers(prev => {
          const next = new Set(prev);
          newlySyncedTickers.forEach((t: string) => next.delete(t));
          return next;
        });
        // lastUpdated is already set from last_sync_ended_at above, don't override it
        
        // Save newly synced tickers to database if authenticated
        // Always save when tickers become synced, even if they're already in tickerList,
        // because the database mappings need to be updated now that they have ticker_basics_id
        if (isAuthenticated && currentListId !== null && newlySyncedTickers.length > 0) {
          const mergedList = Array.from(new Set([...newlySyncedTickers, ...tickerList]));
          await autoSaveList(mergedList);
          console.log(`💾 Saved ${newlySyncedTickers.length} newly synced tickers to database during polling:`, newlySyncedTickers);
        }
        
        console.log(`ListFundamentals: Successfully fetched data for ${newlySyncedTickers.join(', ')}`);
      }

      // If there are still unsynced tickers, continue polling
      if (stillUnsyncedTickers.length > 0) {
        // If we've reached the polling limit, stop polling
        if (attemptNumber >= 5) {
          console.warn(`Sync polling limit reached (5 attempts) for tickers: ${stillUnsyncedTickers.join(', ')}. Stopping polling silently.`);
          setPollingLimitReached(prev => {
            const newSet = new Set(prev);
            stillUnsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
            return newSet;
          });
          stopPolling();
          isCheckingSyncRef.current = false; // Reset flag
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`ListFundamentals: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false; // Reset flag before returning
            return;
          }

          console.log(`Found ${stillUnsyncedTickers.length} still unsynced tickers: ${stillUnsyncedTickers.join(', ')}. Attempt ${attemptNumber}/5.`);
          
          // Use fixed 2-second delay between polling attempts
          const pollingDelay = 2000; // 2 seconds
          
          // Clear any existing interval before creating a new one
          // This ensures we always use the latest attempt number
          clearPollingInterval();
          
          console.log(`Starting sync polling with ${pollingDelay}ms delay`);
          const interval = setInterval(() => {
            // Check if polling was stopped or we've reached the limit
            if (pollingStoppedRef.current) {
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              return;
            }
            
            // Get the next attempt number from the ref
            const nextAttempt = currentAttemptRef.current + 1;
            
            // Check if we've exceeded the limit
            if (nextAttempt > 5) {
              console.log(`ListFundamentals: Reached attempt limit (5), stopping interval`);
              clearInterval(interval);
              syncPollingIntervalRef.current = null;
              pollingStoppedRef.current = true;
              return;
            }
            
            // Update the ref for the next iteration
            currentAttemptRef.current = nextAttempt;
            
            // Check current unsynced tickers from state
            setUnsyncedTickers(currentUnsynced => {
              if (currentUnsynced.size > 0) {
                console.log(`Polling: checking ${currentUnsynced.size} unsynced tickers:`, Array.from(currentUnsynced));
                checkSyncAndFetchDataForTickers(Array.from(currentUnsynced), nextAttempt);
              } else {
                console.log('Polling: no unsynced tickers found, stopping polling');
                clearInterval(interval);
                syncPollingIntervalRef.current = null;
                pollingStoppedRef.current = true;
              }
              return currentUnsynced;
            });
          }, pollingDelay);
          
          // Update the current attempt ref so the interval callback knows the next attempt number
          currentAttemptRef.current = attemptNumber;
          syncPollingIntervalRef.current = interval;
          
          // Reset flag after setting up interval
          isCheckingSyncRef.current = false;
        }
      } else {
        // All tickers synced, stop polling
        console.log('All tickers synced, stopping polling');
        stopPolling();
        isCheckingSyncRef.current = false; // Reset flag
      }

      setError(null);
      isCheckingSyncRef.current = false; // Reset flag on success
    } catch (err) {
      console.error('ListFundamentals: Error in checkSyncAndFetchDataForTickers:', err);
      
      // Check if it's a 429 rate limit error
      const isRateLimitError = 
        (axios.isAxiosError(err) && err.response?.status === 429) ||
        (err instanceof Error && (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')));
      
      if (isRateLimitError) {
        console.warn(`ListFundamentals: Rate limit (429) detected. Stopping polling.`);
        handleRateLimitError();
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(`Failed to check sync status: ${errorMessage}`);
        
        // Stop polling on any error
        stopPolling();
      }
      isCheckingSyncRef.current = false; // Reset flag
    }
  };

  // Function to fetch fundamental data for a single ticker following the correct API sequence
  const fetchFundamentalDataForSingleTicker = async (tickerSymbol: string) => {
    try {
      console.log(`ListFundamentals: Starting single ticker data fetch for: ${tickerSymbol}`);

      // Step 1: Get ticker basics ID from sync status (no need for separate basics API call)
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: [tickerSymbol],
      });
      
      if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers || syncResponse.data.tickers.length === 0) {
        console.error(`Failed to get sync status for ${tickerSymbol}`);
        return;
      }

      const tickerStatus = syncResponse.data.tickers.find((t: any) => t.ticker_symbol === tickerSymbol);
      if (!tickerStatus) {
        console.error(`Ticker ${tickerSymbol} not found in sync status response`);
        return;
      }

      const tickerId = tickerStatus.ticker_basics_id;
      console.log(`ListFundamentals: Got ticker basics ID: ${tickerId} for ${tickerSymbol} from sync status`);

      let currentQuarterRatios: { [tickerId: number]: any } = {};
      let ratiosComparisonData: { [tickerId: number]: any } = {};
      let financialsComparisonData: { [tickerId: number]: any } = {};

      if (timeframe === 'quarterly') {
        // Step 2: For synced ticker, call "current" API once for this ticker
        console.log('ListFundamentals: Fetching current quarterly ratios for single ticker...');
        const currentRatiosResponse = await api.getCurrentQuarterlyRatios([tickerId], isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (currentRatiosResponse.success && currentRatiosResponse.data) {
          currentRatiosResponse.data.forEach((tickerData: any) => {
            currentQuarterRatios[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 3: For synced ticker, call "latest-ended-and-comparisons" once for this ticker
        console.log('ListFundamentals: Fetching latest ended quarterly ratios with comparisons for single ticker...');
        const ratiosResponse = await api.getLatestEndedQuarterlyRatiosWithComparisons([tickerId], isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 4: For synced ticker, call "latest-and-comparisons" once for this ticker
        console.log('ListFundamentals: Fetching latest quarterly financials with comparisons for single ticker...');
        const financialsResponse = await api.getLatestEndedQuarterlyFinancialsWithComparisons([tickerId]);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      } else {
        // Annual timeframe - fetch annual data with comparisons for single ticker
        console.log('ListFundamentals: Fetching annual ratios with comparisons for single ticker...');
        const ratiosResponse = await api.getLatestAnnualRatiosWithComparisons([tickerId], isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        console.log('ListFundamentals: Fetching annual financials with comparisons for single ticker...');
        const financialsResponse = await api.getLatestAnnualFinancialsWithComparisons([tickerId]);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      }

      // Process data for this single ticker
      const symbol = tickerSymbol;
      const currentRatiosData = currentQuarterRatios[tickerId];
      const ratiosData = ratiosComparisonData[tickerId];
      const financialsData = financialsComparisonData[tickerId];
      
      // Log what data we received for debugging
      console.log(`ListFundamentals: Data received for ${symbol}:`, {
        hasCurrentRatios: !!currentRatiosData,
        currentRatiosCount: currentRatiosData?.ratios?.length || 0,
        hasRatiosData: !!ratiosData,
        latestQuarterCount: ratiosData?.latestQuarter?.length || 0,
        hasFinancialsData: !!financialsData,
        financialsCount: financialsData?.latestQuarter?.length || 0,
      });
      
      // Get ticker name from sessionStorage cache (same pattern as other components)
      const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
      const tickerName = cachedName || tickerNames[symbol] || symbol;
      const tickerSymbolValue = cachedSymbol || tickerSymbols[symbol] || symbol;
      
      // Create a map of all fundamental values
      const fundamentalValues: { [key: string]: number } = {};
      const comparisonData: { [key: string]: any } = {};
      
      if (timeframe === 'quarterly') {
        // Process current quarter ratios for Latest Valuation section
        if (currentRatiosData) {
          currentRatiosData.ratios?.forEach((ratio: any) => {
            fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
          });
        }
        
        // Process latest ended quarter ratios for comparison data
        if (ratiosData) {
          ratiosData.latestQuarter?.forEach((ratio: any) => {
            // For Latest Valuation section, compare current quarter vs latest ended quarter
            const currentValue = currentRatiosData?.ratios?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue;
            if (currentValue !== undefined) {
              comparisonData[ratio.ratioKey] = {
                latest: currentValue,
                previous: ratio.ratioValue, // latest ended quarter as comparison
                sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
              };
            } else {
              // For other sections, use latest ended quarter data
              fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
              comparisonData[ratio.ratioKey] = {
                latest: ratio.ratioValue,
                previous: ratiosData.previousQuarter?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
              };
            }
          });
        }
        
        if (financialsData) {
          // Process latest quarter financials
          financialsData.latestQuarter?.forEach((financial: any) => {
            fundamentalValues[financial.statsKey] = financial.statsValue;
            comparisonData[financial.statsKey] = {
              latest: financial.statsValue,
              previous: financialsData.previousQuarter?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
              sameQuarterLastYear: financialsData.sameQuarterLastYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
            };
          });
        }
      }

      if (timeframe === 'annual') {
        // Process annual ratios
        if (ratiosData && ratiosData.latestYear) {
          ratiosData.latestYear.forEach((ratio: any) => {
            fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
            comparisonData[ratio.ratioKey] = {
              latest: ratio.ratioValue,
              previous: ratiosData.previousYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
              sameQuarterLastYear: undefined, // Not used for annual data
            };
          });
        }
        
        if (financialsData && financialsData.latestYear) {
          financialsData.latestYear.forEach((financial: any) => {
            fundamentalValues[financial.statsKey] = financial.statsValue;
            comparisonData[financial.statsKey] = {
              latest: financial.statsValue,
              previous: financialsData.previousYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
              sameQuarterLastYear: undefined, // Not used for annual data
            };
          });
        }
      }

      // Create the ticker display data
      const newTickerData: TickerDisplayData = {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbolValue,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: false,
        _comparisonData: comparisonData,
        _timeframe: timeframe,
        // Add all fundamental values
        ...fundamentalValues,
      };

      // Update the ticker data state
      setTickerData(prev => ({
        ...prev,
        [symbol]: newTickerData,
      }));

      console.log(`ListFundamentals: Successfully fetched single ticker data for ${tickerSymbol}:`, newTickerData);
    } catch (error) {
      console.error(`Error fetching single ticker fundamental data for ${tickerSymbol}:`, error);
    }
  };

  // Optimized function to fetch fundamental data following the correct API sequence
  const fetchFundamentalDataForTickersOptimized = async (tickerSymbols: string[], syncResponseData?: any) => {
    if (tickerSymbols.length === 0) return;

    try {
      console.log(`ListFundamentals: Starting optimized data fetch for: ${tickerSymbols.join(', ')}`);

      const api = isAuthenticated ? userTickerApi : tickerApi;
      let tickerBasicsIds: number[] = [];
      
      if (syncResponseData) {
        // Use provided sync response data to avoid duplicate API call
        console.log('ListFundamentals: Using provided sync response data');
        tickerBasicsIds = syncResponseData.tickers
          .filter((ticker: any) => ticker.is_synced === true && tickerSymbols.includes(ticker.ticker_symbol))
          .map((ticker: any) => ticker.ticker_basics_id);
      } else {
        // Fallback: Get ticker basics IDs from sync status (only if no sync data provided)
        console.log('ListFundamentals: No sync data provided, making API call');
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: tickerSymbols,
        });
        
        if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers || syncResponse.data.tickers.length === 0) {
          console.error(`Failed to get sync status for ${tickerSymbols.join(', ')}`);
          return;
        }

        // Calculate last updated from latest last_sync_ended_at of synced tickers
        const syncedTickersWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickersWithDate.length > 0) {
          const lastSyncDates = syncedTickersWithDate
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }

        tickerBasicsIds = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_basics_id);
      }
      
      if (tickerBasicsIds.length === 0) {
        console.log(`No synced tickers found for ${tickerSymbols.join(', ')}`);
        return;
      }
      
      console.log(`ListFundamentals: Got ticker basics IDs from sync status: ${tickerBasicsIds.join(', ')}`);

      let currentQuarterRatios: { [tickerId: number]: any } = {};
      let ratiosComparisonData: { [tickerId: number]: any } = {};
      let financialsComparisonData: { [tickerId: number]: any } = {};

      if (timeframe === 'quarterly') {
        // Step 2: For synced tickers, call "current" API once with multiple tickers
        console.log('ListFundamentals: Fetching current quarterly ratios...');
        const currentRatiosResponse = await api.getCurrentQuarterlyRatios(tickerBasicsIds, isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (currentRatiosResponse.success && currentRatiosResponse.data) {
          currentRatiosResponse.data.forEach((tickerData: any) => {
            currentQuarterRatios[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 3: For synced tickers, call "latest-ended-and-comparisons" once with multiple tickers
        console.log('ListFundamentals: Fetching latest ended quarterly ratios with comparisons...');
        const ratiosResponse = await api.getLatestEndedQuarterlyRatiosWithComparisons(tickerBasicsIds, isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        // Step 4: For synced tickers, call "latest-and-comparisons" once with multiple tickers
        console.log('ListFundamentals: Fetching latest quarterly financials with comparisons...');
        const financialsResponse = await api.getLatestEndedQuarterlyFinancialsWithComparisons(tickerBasicsIds);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      } else {
        // Annual timeframe - fetch annual data with comparisons
        console.log('ListFundamentals: Fetching annual ratios with comparisons...');
        const ratiosResponse = await api.getLatestAnnualRatiosWithComparisons(tickerBasicsIds, isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined);
        
        if (ratiosResponse.success && ratiosResponse.data) {
          ratiosResponse.data.forEach((tickerData: any) => {
            ratiosComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }

        console.log('ListFundamentals: Fetching annual financials with comparisons...');
        const financialsResponse = await api.getLatestAnnualFinancialsWithComparisons(tickerBasicsIds);
        
        if (financialsResponse.success && financialsResponse.data) {
          financialsResponse.data.forEach((tickerData: any) => {
            financialsComparisonData[tickerData.tickerBasicsId] = tickerData;
          });
        }
      }

      // Process data for each ticker
      const newTickerData: { [symbol: string]: TickerDisplayData } = {};
      
      // Use syncResponseData if available, otherwise we need to get ticker data differently
      const tickerStatuses = syncResponseData ? syncResponseData.tickers : [];
      tickerStatuses.forEach((tickerStatus: any) => {
        const symbol = tickerStatus.ticker_symbol;
        const tickerId = tickerStatus.ticker_basics_id;
        const currentRatiosData = currentQuarterRatios[tickerId];
        const ratiosData = ratiosComparisonData[tickerId];
        const financialsData = financialsComparisonData[tickerId];
        
        // Create a map of all fundamental values
        const fundamentalValues: { [key: string]: number } = {};
        const comparisonData: { [key: string]: any } = {};
        
        if (timeframe === 'quarterly') {
          // Process current quarter ratios for Latest Valuation section
          if (currentRatiosData) {
            currentRatiosData.ratios?.forEach((ratio: any) => {
              fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
            });
          }
          
          // Process latest ended quarter ratios for comparison data
          if (ratiosData) {
            ratiosData.latestQuarter?.forEach((ratio: any) => {
              // For Latest Valuation section, compare current quarter vs latest ended quarter
              const currentValue = currentRatiosData?.ratios?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue;
              if (currentValue !== undefined) {
                comparisonData[ratio.ratioKey] = {
                  latest: currentValue,
                  previous: ratio.ratioValue, // latest ended quarter as comparison
                  sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                };
              } else {
                // For other sections, use latest ended quarter data
                fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
                comparisonData[ratio.ratioKey] = {
                  latest: ratio.ratioValue,
                  previous: ratiosData.previousQuarter?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                  sameQuarterLastYear: ratiosData.sameQuarterLastYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                };
              }
            });
          }
          
          if (financialsData) {
            // Process latest quarter financials
            financialsData.latestQuarter?.forEach((financial: any) => {
              fundamentalValues[financial.statsKey] = financial.statsValue;
              comparisonData[financial.statsKey] = {
                latest: financial.statsValue,
                previous: financialsData.previousQuarter?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                sameQuarterLastYear: financialsData.sameQuarterLastYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
              };
            });
          }
        }

        if (timeframe === 'annual') {
          // Process annual ratios
          if (ratiosData && ratiosData.latestYear) {
            ratiosData.latestYear.forEach((ratio: any) => {
              fundamentalValues[ratio.ratioKey] = ratio.ratioValue;
              comparisonData[ratio.ratioKey] = {
                latest: ratio.ratioValue,
                previous: ratiosData.previousYear?.find((r: any) => r.ratioKey === ratio.ratioKey)?.ratioValue,
                sameQuarterLastYear: undefined, // Not used for annual data
              };
            });
          }
          
          if (financialsData && financialsData.latestYear) {
            financialsData.latestYear.forEach((financial: any) => {
              fundamentalValues[financial.statsKey] = financial.statsValue;
              comparisonData[financial.statsKey] = {
                latest: financial.statsValue,
                previous: financialsData.previousYear?.find((f: any) => f.statsKey === financial.statsKey)?.statsValue,
                sameQuarterLastYear: undefined, // Not used for annual data
              };
            });
          }
        }

        // Get ticker name from sessionStorage cache (same pattern as other components)
        const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
        const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
        const tickerName = cachedName || tickerNames[symbol] || symbol;
        const tickerSymbol = cachedSymbol || tickerSymbols[symbol] || symbol;

        // Create the ticker display data
        newTickerData[symbol] = {
          ticker: symbol,
          name: symbol,
          ticker_name: tickerName,
          ticker_symbol: tickerSymbol,
          latest: 0,
          change: 0,
          changePercent: 0,
          volume: 0,
          high: 0,
          low: 0,
          open: 0,
          prevClose: 0,
          updated: Date.now(),
          isSyncing: false,
          _comparisonData: comparisonData,
          _timeframe: timeframe,
          // Add all fundamental values
          ...fundamentalValues,
        };
      });

      // Update the ticker data state
      setTickerData(prev => ({
        ...prev,
        ...newTickerData,
      }));

      console.log(`ListFundamentals: Successfully fetched optimized data for ${tickerSymbols.join(', ')}:`, newTickerData);
    } catch (error) {
      console.error(`Error fetching optimized fundamental data for ${tickerSymbols.join(', ')}:`, error);
    }
  };



  // Fetch fundamental data
  const fetchFundamentalData = async () => {
    if (tickerList.length === 0 || isLoading) return;

    console.log('ListFundamentals: fetchFundamentalData called (onRefetch)');
    setIsLoading(true);
    setError(null);

    try {
      // Use the optimized approach for fetching data
      await fetchFundamentalDataForTickersOptimized(tickerList);
      // Note: lastUpdated should be set from last_sync_ended_at in checkSyncAndFetchDataForTickers
    } catch (error) {
      console.error('Error fetching fundamental data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Check if it's a rate limit error (429)
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        handleRateLimitError();
      } else {
        setError('Failed to load fundamental data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTickerClick = (tickerSymbol: string) => {
    const timeframeParam = `timeframe=${timeframe}`;
    if (isPublicList && publicUuid) {
      navigate(`/ticker/${tickerSymbol}/fundamentals?public_uuid=${publicUuid}&${timeframeParam}`);
    } else {
      navigate(`/ticker/${tickerSymbol}/fundamentals?${timeframeParam}`);
    }
  };

  // Get ticker data for display
  const getTickerData = (symbol: string): TickerDisplayData | undefined => {
    const isSyncingTicker = syncingTickers.has(symbol);
    const isUnsyncedTicker = unsyncedTickers.has(symbol);
    const tickerDataForSymbol = tickerData[symbol];
    
    // Get ticker name from sessionStorage cache (same pattern as other components)
    const cachedName = sessionStorage.getItem(`ticker-name-${symbol}`);
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${symbol}`);
    const tickerName = cachedName || tickerNames[symbol] || symbol;
    const tickerSymbol = cachedSymbol || tickerSymbols[symbol] || symbol;
    
    
    // Show loading animation for syncing tickers, unsynced tickers, or when no data has been fetched yet
    if (isSyncingTicker || isUnsyncedTicker || !tickerDataForSymbol) {
      return {
        ticker: symbol,
        name: symbol,
        ticker_name: tickerName,
        ticker_symbol: tickerSymbol,
        latest: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        prevClose: 0,
        updated: Date.now(),
        isSyncing: isSyncingTicker || isUnsyncedTicker || !tickerDataForSymbol,
      };
    }
    
    return {
      ...tickerDataForSymbol,
      ticker_name: tickerName,
      ticker_symbol: tickerSymbol,
      isSyncing: isSyncingTicker || isUnsyncedTicker,
    };
  };



  // Handle category info click
  const handleCategoryInfoClick = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };

  // Handle indicator info click
  const handleIndicatorInfoClick = (indicatorKey: string) => {
    setSelectedIndicatorKey(indicatorKey);
    setIsIndicatorModalOpen(true);
  };

  // Handle indicator modal close
  const handleCloseIndicatorModal = () => {
    setIsIndicatorModalOpen(false);
    setSelectedIndicatorKey('');
  };


  // Statistics handlers


  // Ticker management functions using useAddTicker hook
  const { handleAddTicker } = useAddTicker({
    isAuthenticated,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList: tempTickerList || [],
    setTempTickerList: setTempTickerList as React.Dispatch<React.SetStateAction<string[]>> | undefined,
    isEditingTickers,
    initializeEmptyData: useCallback((ticker: string) => {
      // Get ticker name from cache (will be updated after sync API returns)
      const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
      const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
      const tickerName = cachedName || tickerNames[ticker] || ticker;
      const tickerSymbol = cachedSymbol || tickerSymbols[ticker] || ticker;
      
      setTickerData(prev => ({
        ...prev,
        [ticker]: {
          ticker: ticker,
          name: ticker,
          ticker_name: tickerName,
          ticker_symbol: tickerSymbol,
          latest: 0,
          change: 0,
          changePercent: 0,
          volume: 0,
          high: 0,
          low: 0,
          open: 0,
          prevClose: 0,
          updated: Date.now(),
          isSyncing: true,
        }
      }));
    }, [tickerNames, tickerSymbols]),
    onTickerNameUpdate: useCallback((ticker: string, tickerName: string, tickerSymbol?: string) => {
      if (tickerName) {
        setTickerNames(prev => ({
          ...prev,
          [ticker]: tickerName
        }));
        sessionStorage.setItem(`ticker-name-${ticker}`, tickerName);
      }
      if (tickerSymbol) {
        setTickerSymbols(prev => ({
          ...prev,
          [ticker]: tickerSymbol
        }));
        sessionStorage.setItem(`ticker-symbol-${ticker}`, tickerSymbol);
      }
    }, []),
    onSyncComplete: useCallback(async (ticker: string) => {
      await fetchFundamentalDataForSingleTicker(ticker);
    }, [fetchFundamentalDataForSingleTicker]),
    autoSaveList,
    saveListImmediately: useCallback(async (tickers: string[]) => {
      const uniqueTickers = Array.from(new Set(tickers));
      if (isPublicList) return;

      if (isAuthenticated && !isPublicList) {
        let listId = currentListId;
        if (listId === null) {
          const cachedListId = getCachedCurrentListId();
          if (cachedListId) listId = cachedListId;
          else if (userLists.length > 0) {
            try {
              const defaultListResponse = await userApi.getDefaultList();
              if (defaultListResponse.defaultUserTickerListId && userLists.some((l: UserTickerList) => l.id === defaultListResponse.defaultUserTickerListId)) {
                listId = defaultListResponse.defaultUserTickerListId;
              } else {
                listId = userLists[0].id;
              }
            } catch {
              listId = userLists[0].id;
            }
          }
        }
        if (listId != null) {
          try {
            const tickerMappings = uniqueTickers.map((ticker, index) => ({ ticker_symbol: ticker, order_num: index + 1 }));
            await userTickerListApi.updateTickerMappings(listId, tickerMappings);
            clearListCache(listId);
            needsListDataReload.current = true;
          } catch (error) {
            console.error('saveListImmediately: failed to save to server', error);
          }
        }
        return;
      }

      sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueTickers));
    }, [getTickerListKey, currentListId, isPublicList, isAuthenticated, userLists]),
    pollingInterval: 1000,
    maxPollAttempts: 10,
    checkAlreadyExists: true,
    setIsAddingNewTicker,
    normalizeTicker: (ticker: string) => ticker.toUpperCase(),
  });

  const handleRemoveTicker = async (ticker: string) => {
    if (isEditingTickers) {
      const newTempList = tempTickerList.filter(t => t !== ticker);
      setTempTickerList(newTempList);
      // No auto-save in edit mode - will be saved when "Done" is clicked
    } else {
      const newList = tickerList.filter(t => t !== ticker);
      setTickerList(newList);
      
      // Auto-save for authenticated users (only when not in edit mode)
      if (isAuthenticated) {
        await autoSaveList(newList);
      }
    }
  };



  // Format statistic value for display using shared utility
  const formatStatisticValue = (statisticKey: string, ticker: TickerDisplayData): React.ReactNode => {
    // Check if this statistic is locked for non-pro users BEFORE formatting
    // If the value is null and the statistic is locked, show lock icon
    if (lockedStatistics.includes(statisticKey)) {
      const value = (ticker as any)[statisticKey];
      // If value is null (locked), show lock icon instead of formatting
      if (value === null || value === undefined) {
        return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
      }
    }
    
    return formatFundamentalsStatisticValue(statisticKey, ticker, {
      timeframe,
      syncingTickers,
      unsyncedTickers,
      pollingLimitReached,
    });
  };

  // Get statistic color based on value using shared utility
  const getStatisticColor = (statisticKey: string, ticker: TickerDisplayData): string => {
    return getFundamentalsStatisticColor(statisticKey, ticker, {
      timeframe,
    });
  };



  // Initial data fetch and refetch when tickerList or timeframe changes
  // Only fetch data for tickers that don't already have data loaded
  useEffect(() => {
    if (tickerList.length === 0 || isAddingNewTicker) return;
    
    // Reset polling state on initial mount or when dependencies change
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    clearPollingInterval();
    
    const checkInitialSyncStatus = async () => {
      console.log('ListFundamentals: useEffect triggered with dependencies:', { tickerList: tickerList.length, timeframe, isAddingNewTicker });
      try {
        console.log('ListFundamentals: checkInitialSyncStatus called');
        
        // Check which tickers already have data loaded
        const tickersNeedingData = tickerList.filter(symbol => {
          const existingData = tickerData[symbol];
          // Check if ticker has no data or if timeframe changed (data might be outdated)
          // Only check timeframe if it exists (for backward compatibility with existing data)
          return !existingData || !existingData._comparisonData || 
                 (existingData._timeframe && existingData._timeframe !== timeframe);
        });
        
        console.log('ListFundamentals: Tickers needing data:', tickersNeedingData);
        console.log('ListFundamentals: Tickers with existing data:', tickerList.filter(symbol => !tickersNeedingData.includes(symbol)));
        
        // If no tickers need data, skip the API calls
        if (tickersNeedingData.length === 0) {
          console.log('ListFundamentals: All tickers already have data, skipping API calls');
          return;
        }
        
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: tickersNeedingData,
        });
        console.log('ListFundamentals: Initial sync check response:', syncResponse);

        if (!syncResponse.success || !syncResponse.data || !syncResponse.data.tickers) {
          console.error('ListFundamentals: Invalid sync response structure:', syncResponse);
          setError('Invalid response from sync status check');
          return;
        }

        // Calculate last updated from latest last_sync_ended_at of synced tickers
        const syncedTickersWithDate = syncResponse.data.tickers.filter((t: any) => t.is_synced === true && t.last_sync_ended_at);
        if (syncedTickersWithDate.length > 0) {
          const lastSyncDates = syncedTickersWithDate
            .map((t: any) => {
              const date = typeof t.last_sync_ended_at === 'string' 
                ? new Date(t.last_sync_ended_at) 
                : t.last_sync_ended_at;
              return date instanceof Date && !isNaN(date.getTime()) ? date : null;
            })
            .filter((d: Date | null): d is Date => d !== null);
          
          if (lastSyncDates.length > 0) {
            const latestDate = new Date(Math.max(...lastSyncDates.map((d: Date) => d.getTime())));
            setLastUpdated(latestDate);
          }
        }
        
        // Store ticker names and symbols from sync response
        const newTickerNames: { [symbol: string]: string } = {};
        const newTickerSymbols: { [symbol: string]: string } = {};
        syncResponse.data.tickers.forEach((ticker: any) => {
          if (ticker.ticker_name) {
            newTickerNames[ticker.ticker_symbol] = ticker.ticker_name;
            sessionStorage.setItem(`ticker-name-${ticker.ticker_symbol}`, ticker.ticker_name);
          }
          if (ticker.ticker_symbol) {
            newTickerSymbols[ticker.ticker_symbol] = ticker.ticker_symbol;
            sessionStorage.setItem(`ticker-symbol-${ticker.ticker_symbol}`, ticker.ticker_symbol);
          }
        });
        setTickerNames(prev => ({ ...prev, ...newTickerNames }));
        setTickerSymbols(prev => ({ ...prev, ...newTickerSymbols }));

        const syncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === true)
          .map((ticker: any) => ticker.ticker_symbol);
        
        const unsyncedTickers = syncResponse.data.tickers
          .filter((ticker: any) => ticker.is_synced === false)
          .map((ticker: any) => ticker.ticker_symbol);

        console.log('ListFundamentals: syncedTickers:', syncedTickers);
        console.log('ListFundamentals: unsyncedTickers:', unsyncedTickers);

        // Update sync status sets
        setSyncedTickers(prev => {
          const newSet = new Set(prev);
          syncedTickers.forEach((ticker: string) => newSet.add(ticker));
          return newSet;
        });
        
        setUnsyncedTickers(prev => {
          const newSet = new Set(prev);
          unsyncedTickers.forEach((ticker: string) => newSet.add(ticker));
          // Remove newly synced tickers from unsynced set
          syncedTickers.forEach((ticker: string) => newSet.delete(ticker));
          return newSet;
        });

        // Fetch data for synced tickers that need data immediately using optimized batched approach
        const syncedTickersNeedingData = syncedTickers.filter((symbol: string) => tickersNeedingData.includes(symbol));
        if (syncedTickersNeedingData.length > 0) {
          console.log(`ListFundamentals: Fetching data for synced tickers that need data: ${syncedTickersNeedingData.join(', ')}`);
          await fetchFundamentalDataForTickersOptimized(syncedTickersNeedingData, syncResponse.data);
          // Safety: data fetched => clear any lingering syncing flags so UI doesn't stay pulsing.
          setSyncingTickers(prev => {
            const next = new Set(prev);
            syncedTickersNeedingData.forEach((t: string) => next.delete(t));
            return next;
          });
          setUnsyncedTickers(prev => {
            const next = new Set(prev);
            syncedTickersNeedingData.forEach((t: string) => next.delete(t));
            return next;
          });
          // lastUpdated is already set from last_sync_ended_at above, don't override it
          console.log(`ListFundamentals: Successfully fetched data for ${syncedTickersNeedingData.join(', ')}`);
        } else {
          console.log('ListFundamentals: All synced tickers already have data, skipping data fetch');
        }

        // If there are unsynced tickers that need data, start polling for them immediately
        const unsyncedTickersNeedingData = unsyncedTickers.filter((symbol: string) => tickersNeedingData.includes(symbol));
        if (unsyncedTickersNeedingData.length > 0) {
          console.log(`ListFundamentals: Found unsynced tickers that need data: ${unsyncedTickersNeedingData.join(', ')}`);
          console.log('ListFundamentals: Starting immediate check for unsynced tickers that need data');
          
          // Reset polling state when starting new polling
          pollingStoppedRef.current = false;
          currentAttemptRef.current = 1; // Reset attempt counter
          isCheckingSyncRef.current = false; // Reset concurrent check flag
          
          // Only start polling if we don't already have an active polling interval
          if (!syncPollingIntervalRef.current) {
            setTimeout(() => {
              console.log('ListFundamentals: Executing immediate check for unsynced tickers that need data');
              checkSyncAndFetchDataForTickers(unsyncedTickersNeedingData, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          } else {
            console.log('ListFundamentals: Polling already active, skipping new polling start');
          }
        } else {
          console.log('ListFundamentals: No unsynced tickers need data, skipping polling');
        }

        // Clear loading state after data fetch completes (for timeframe changes)
        setIsLoading(false);
        setError(null);
      } catch (error) {
        console.error('Error in initial sync check:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        
        // Clear loading state even on error
        setIsLoading(false);
        
        // Check if it's a rate limit error (429)
        if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
          handleRateLimitError();
        } else {
          setError(`Failed to check initial sync status: ${errorMessage}`);
        }
      }
    };

    checkInitialSyncStatus();
  }, [tickerList, timeframe, isAddingNewTicker]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Register current state with the global session save service
  useEffect(() => {
    if (!isAuthenticated) {
      sessionSaveService.registerSessionData({
        tickerList,
        listName,
        hiddenStatistics: {
          fundamentals: hiddenStatistics
        }
      });
    }
  }, [tickerList, listName, hiddenStatistics, isAuthenticated]);

  // Persist syncingTickers to sessionStorage whenever it changes
  // Use list ID-specific key for authenticated users
  // Also migrate from global key to list ID-specific key if currentListId becomes available
  useEffect(() => {
    if (!isAuthenticated || currentListId === null) {
      // For unauthenticated users, use global key
      if (syncingTickers.size > 0) {
        sessionStorage.setItem('stocktable-syncing-tickers', JSON.stringify(Array.from(syncingTickers)));
      } else {
        sessionStorage.removeItem('stocktable-syncing-tickers');
      }
      return;
    }
    
    // For authenticated users, use list ID-specific key
    const key = getSyncingTickersKey();
    
    // Migrate from global key to list ID-specific key on first use
    const globalKey = 'stocktable-syncing-tickers';
    const globalSaved = sessionStorage.getItem(globalKey);
    if (globalSaved && syncingTickers.size === 0) {
      // If we have syncing tickers in global key but not in state, restore them
      try {
        const globalTickers = JSON.parse(globalSaved);
        setSyncingTickers(new Set(globalTickers));
        // Clear global key after migration
        sessionStorage.removeItem(globalKey);
      } catch (error) {
        console.error('Error migrating syncing tickers:', error);
      }
    }
    
    if (syncingTickers.size > 0) {
      sessionStorage.setItem(key, JSON.stringify(Array.from(syncingTickers)));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [syncingTickers, getSyncingTickersKey, isAuthenticated, currentListId]);

  // Restore syncing state for tickers that were syncing before refresh (only on initial load)
  useEffect(() => {
    // Only run once on initial load, not when new tickers are added during the session
    if (hasRestoredSyncingStateRef.current) return;
    
    // Wait for tickerList to be loaded
    if (tickerList.length === 0) return;
    
    const restoreSyncingState = async () => {
      // Double-check flag to prevent race conditions
      if (hasRestoredSyncingStateRef.current) return;
      
      // Get tickers that were syncing from sessionStorage and are in current list
      const syncingArray = Array.from(syncingTickers).filter(ticker => tickerList.includes(ticker));
      if (syncingArray.length === 0) {
        // Mark as restored even if no tickers to restore
        hasRestoredSyncingStateRef.current = true;
        return;
      }
      
      // Mark as restored before making the API call to prevent duplicate calls
      hasRestoredSyncingStateRef.current = true;
      
      try {
        console.log(`🔄 Restoring sync status for ${syncingArray.length} tickers that were syncing before refresh:`, syncingArray);
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: syncingArray,
        });
        
        const syncedTickers: string[] = [];
        const stillSyncingTickers: string[] = [];
        
        syncResponse.data?.tickers?.forEach((t: any) => {
          if (t.is_synced) {
            syncedTickers.push(t.ticker_symbol);
          } else {
            stillSyncingTickers.push(t.ticker_symbol);
          }
        });
        
        // Remove synced tickers from syncing set and unsynced set, then fetch their data
        if (syncedTickers.length > 0) {
          setSyncingTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          // Also remove from unsyncedTickers to ensure they're not shown as loading
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            syncedTickers.forEach(ticker => newSet.delete(ticker));
            return newSet;
          });
          console.log(`✅ ${syncedTickers.length} tickers finished syncing:`, syncedTickers);
          
          // Fetch data for synced tickers
          const syncedTickerStatuses = syncResponse.data?.tickers?.filter((t: any) => 
            syncedTickers.includes(t.ticker_symbol)
          ) || [];
          
          if (syncedTickerStatuses.length > 0) {
            console.log(`🔄 Fetching data for ${syncedTickers.length} restored synced tickers:`, syncedTickers);
            setTimeout(async () => {
              try {
                // Fetch fundamental data using optimized batched approach
                await fetchFundamentalDataForTickersOptimized(syncedTickers, syncResponse.data);
                // Safety: ensure restored tickers don't remain in syncing/unsynced pulse after data is fetched.
                setSyncingTickers(prev => {
                  const next = new Set(prev);
                  syncedTickers.forEach((t: string) => next.delete(t));
                  return next;
                });
                setUnsyncedTickers(prev => {
                  const next = new Set(prev);
                  syncedTickers.forEach((t: string) => next.delete(t));
                  return next;
                });
                // lastUpdated is already set from last_sync_ended_at above, don't override it
                
                // Save synced tickers to database if authenticated
                if (isAuthenticated && currentListId !== null && syncedTickers.length > 0) {
                  const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
                  
                  // Only save if the merged list is actually different from current tickerList
                  // This prevents unnecessary API calls when tickers are already in the list
                  const currentListSet = new Set(tickerList);
                  const isDifferent = mergedList.length !== tickerList.length || 
                                     !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
                  
                  if (isDifferent) {
                    await autoSaveList(mergedList);
                    console.log(`💾 Saved ${syncedTickers.length} restored synced tickers to database:`, syncedTickers);
                  } else {
                    console.log(`ℹ️ No changes needed - tickers are already in the list`);
                  }
                }
                
                console.log(`✅ Successfully fetched data for restored synced tickers:`, syncedTickers);
              } catch (error) {
                console.error('❌ Error fetching data for restored synced tickers:', error);
              }
            }, 300); // Small delay to avoid rate limiting
          }
        }
        
        // Keep still-syncing tickers in the set and add them to unsyncedTickers to resume polling
        if (stillSyncingTickers.length > 0) {
          console.log(`⏳ ${stillSyncingTickers.length} tickers still syncing, resuming polling:`, stillSyncingTickers);
          
          // Reset polling state to ensure polling can resume
          pollingStoppedRef.current = false;
          currentAttemptRef.current = 1;
          isCheckingSyncRef.current = false;
          
          // Add to unsyncedTickers to trigger polling
          setUnsyncedTickers(prev => {
            const newSet = new Set(prev);
            stillSyncingTickers.forEach(ticker => newSet.add(ticker));
            return newSet;
          });
          
          // Start polling for these tickers
          setTimeout(() => {
            console.log('🔄 Starting polling for restored syncing tickers:', stillSyncingTickers);
            checkSyncAndFetchDataForTickers(stillSyncingTickers, 1);
          }, 1000); // 1 second delay to avoid rate limiting
        }
      } catch (error) {
        console.error('❌ Error checking sync status for syncing tickers:', error);
      }
    };

    // Run once after tickerList is loaded (with a delay to ensure it's fully loaded)
    const timeoutId = setTimeout(restoreSyncingState, 500);
    return () => clearTimeout(timeoutId);
  }, [tickerList, syncingTickers, isAuthenticated, currentListId, setUnsyncedTickers, checkSyncAndFetchDataForTickers, autoSaveList]); // Only run when tickerList is first loaded

  // Recovery: Merge tickers from sessionStorage into tickerList immediately (for authenticated users)
  // This ensures tickers that were added but not yet in database appear immediately with loading state
  useEffect(() => {
    const mergeSessionStorageTickers = () => {
      if (!isAuthenticated || currentListId === null) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only merge tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) return;
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        // Deduplicate sessionStorage tickers first
        const uniqueSessionTickers = Array.from(new Set(sessionTickers));
        // Deduplicate current tickerList
        const uniqueTickerList = Array.from(new Set(tickerList));
        // Find tickers that are in sessionStorage but not in current list
        const missingTickers = uniqueSessionTickers.filter(ticker => !uniqueTickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // If no missing tickers but sessionStorage has duplicates, clean it up
          if (uniqueSessionTickers.length !== sessionTickers.length) {
            sessionStorage.setItem(getTickerListKey(), JSON.stringify(uniqueSessionTickers));
            console.log(`🔧 Cleaned up duplicates in sessionStorage: ${sessionTickers.length} -> ${uniqueSessionTickers.length}`);
          }
          return;
        }
        
        console.log(`🔄 Found ${missingTickers.length} tickers in sessionStorage not in database list:`, missingTickers);
        
        // Preserve order from sessionStorage: merge by keeping sessionStorage order and inserting missing tickers
        // at their original positions, then appending any remaining tickers from database list
        const mergedList: string[] = [];
        
        // First, add all tickers from sessionStorage in their original order
        for (const ticker of uniqueSessionTickers) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        // Then, add any tickers from database list that weren't in sessionStorage
        for (const ticker of uniqueTickerList) {
          if (!mergedList.includes(ticker)) {
            mergedList.push(ticker);
          }
        }
        
        setTickerList(mergedList);
        // Also save the deduplicated merged list back to sessionStorage
        sessionStorage.setItem(getTickerListKey(), JSON.stringify(mergedList));
        console.log(`✅ Merged ${missingTickers.length} tickers from sessionStorage into list, preserving order`);
        
        // Also restore syncing state for these tickers if they were syncing
        // Use list ID-specific key to only restore syncing state for the current list
        const savedSyncing = sessionStorage.getItem(getSyncingTickersKey());
        if (savedSyncing) {
          const syncingArray: string[] = JSON.parse(savedSyncing);
          const syncingTickersToRestore = syncingArray.filter(ticker => missingTickers.includes(ticker));
          if (syncingTickersToRestore.length > 0) {
            setSyncingTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            // Also add to unsyncedTickers so polling can resume
            setUnsyncedTickers(prev => {
              const newSet = new Set(prev);
              syncingTickersToRestore.forEach(ticker => newSet.add(ticker));
              return newSet;
            });
            console.log(`✅ Restored syncing state for ${syncingTickersToRestore.length} tickers:`, syncingTickersToRestore);
            
            // Start polling for these tickers immediately
            // Reset polling state to ensure polling can resume
            pollingStoppedRef.current = false;
            currentAttemptRef.current = 1;
            isCheckingSyncRef.current = false;
            
            // Start polling for these tickers
            setTimeout(() => {
              console.log('🔄 Starting polling for merged syncing tickers:', syncingTickersToRestore);
              checkSyncAndFetchDataForTickers(syncingTickersToRestore, 1);
            }, 1000); // 1 second delay to avoid rate limiting
          }
        }
      } catch (error) {
        console.error('❌ Error merging sessionStorage tickers:', error);
      }
    };

    // Run after tickerList is loaded from database (with a small delay to ensure database list is loaded)
    const timeoutId = setTimeout(mergeSessionStorageTickers, 100);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, setTickerList, setSyncingTickers, setUnsyncedTickers, getTickerListKey, getSyncingTickersKey, checkSyncAndFetchDataForTickers]);

  // Recovery: Check for tickers in sessionStorage that need to be synced to database
  useEffect(() => {
    // Only run once per list load to prevent unnecessary API calls
    if (hasRecoveredPendingTickersRef.current) return;
    
    const recoverPendingTickers = async () => {
      if (!isAuthenticated || !currentListId || tickerList.length === 0) return;
      
      // Check sessionStorage for tickers that might not be in database yet
      // Use list ID-specific key to only recover tickers for the current list
      const savedTickers = sessionStorage.getItem(getTickerListKey());
      if (!savedTickers) {
        hasRecoveredPendingTickersRef.current = true;
        return;
      }
      
      try {
        const sessionTickers: string[] = JSON.parse(savedTickers);
        const missingTickers = sessionTickers.filter(ticker => !tickerList.includes(ticker));
        
        if (missingTickers.length === 0) {
          // No missing tickers - mark as recovered and clear sessionStorage if everything is already saved
          hasRecoveredPendingTickersRef.current = true;
          // Clear sessionStorage since everything is already in the database
          sessionStorage.removeItem(getTickerListKey());
          return;
        }
        
        console.log(`🔄 Checking sync status for ${missingTickers.length} tickers in sessionStorage:`, missingTickers);
        
        // Check if these tickers have been synced (exist in tickerBasics)
        const api = userTickerApi;
        const syncResponse = await api.checkTickerSyncStatus({
          ticker_symbols: missingTickers,
        });
        
        const syncedTickers = syncResponse.data?.tickers
          ?.filter((t: any) => t.is_synced === true)
          .map((t: any) => t.ticker_symbol) || [];
        
        if (syncedTickers.length === 0) {
          console.log('⚠️ No missing tickers are synced yet, will retry later');
          // Don't mark as recovered yet - we'll retry later
          return;
        }
        
        // Mark as recovered since we're processing the recovery now
        hasRecoveredPendingTickersRef.current = true;
        
        console.log(`✅ Found ${syncedTickers.length} synced tickers that need to be saved:`, syncedTickers);
        
        // Merge synced tickers with current list and save to database
        const mergedList = Array.from(new Set([...syncedTickers, ...tickerList]));
        
        // Only save if the merged list is actually different from current tickerList
        // This prevents unnecessary API calls on page refresh when nothing has changed
        const currentListSet = new Set(tickerList);
        const isDifferent = mergedList.length !== tickerList.length || 
                           !syncedTickers.every((ticker: string) => currentListSet.has(ticker));
        
        if (isDifferent) {
          await autoSaveList(mergedList);
          console.log(`💾 Recovered and saved ${syncedTickers.length} tickers to database`);
          // Update local state
          setTickerList(mergedList);
          // Clear sessionStorage after successful save
          sessionStorage.removeItem(getTickerListKey());
        } else {
          console.log(`ℹ️ No changes needed - all tickers are already in the list`);
          // Clear sessionStorage since everything is already saved
          sessionStorage.removeItem(getTickerListKey());
        }
      } catch (error) {
        console.error('❌ Error recovering pending tickers:', error);
        // Mark as recovered even on error to prevent infinite retries
        hasRecoveredPendingTickersRef.current = true;
      }
    };

    // Only run recovery once after initial load, with a delay to ensure database list is loaded
    const timeoutId = setTimeout(recoverPendingTickers, 1500);
    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, currentListId, tickerList, autoSaveList, setTickerList, getTickerListKey]);
  
  // Reset recovery flag when list ID changes
  useEffect(() => {
    hasRecoveredPendingTickersRef.current = false;
  }, [currentListId]);

  // Handle refresh usage limits callback
  const handleRefreshUsageLimits = (refreshFn: () => void) => {
    refreshUsageLimitsRef.current = refreshFn;
  };

  // Share modal handlers
  const handleShareList = () => {
    setIsShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  const handleShareModalUpdate = async () => {
    // Refresh user lists to get updated share settings
    if (isAuthenticated && currentListId) {
      try {
        const updatedLists = await userTickerListApi.getUserLists();
        setUserLists(updatedLists);
        const updatedList = updatedLists.find(list => list.id === currentListId);
        if (updatedList) {
          cacheUserLists(updatedLists);
        }
      } catch (error) {
        console.error('Failed to refresh lists after share update:', error);
      }
    }
  };

  // Get current list data for share modal
  const currentList = currentListId && userLists
    ? userLists.find(list => list.id === currentListId) || null
    : null;

  // Modals hook (must be after handleListChange is defined)
  const {
    isDeleteModalOpen,
    isErrorDialogOpen,
    errorMessage,
    handleDeleteList,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleErrorDialogClose,
  } = useModals({
    currentListId,
    isAuthenticated,
    userLists,
    needsUserListsReload,
    handleListChange,
    refreshUsageLimitsRef,
    onAfterDelete: () => {
      setCurrentListId(null);
      setListName('Main List');
      setTickerList([]);
      setHiddenStatistics([]);
    },
  });

  const tabs = (
    <TabBar 
      activeTab="fundamentals" 
      disabled={isEditingTickers} 
      publicUuid={publicUuid}
    />
  );

  // Handle timeframe change - immediately reset data and show loading
  const handleTimeframeChange = (newTimeframe: 'quarterly' | 'annual') => {
    if (newTimeframe === timeframe) return; // No change needed
    
    // Immediately clear all ticker data to show loading animation
    setTickerData({});
    // Set loading state to show pulsing animation
    setIsLoading(true);
    // Clear any errors
    setError(null);
    // Update timeframe - this will trigger useEffect to fetch new data
    setTimeframe(newTimeframe);
  };

  // Timeframe toggle component
  const timeframeToggle = (
    <div className="flex items-center">
      <div className="relative inline-flex rounded-md bg-gray-200 dark:bg-border-color p-1">
        <button
          onClick={() => handleTimeframeChange('quarterly')}
          disabled={isEditingTickers}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
            timeframe === 'quarterly'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Quarterly
        </button>
        <button
          onClick={() => handleTimeframeChange('annual')}
          disabled={isEditingTickers}
          className={`relative px-3 py-1.5 ${getTextSizeClass(fontSize)} font-semibold rounded-md transition-all duration-200 focus:outline-none ${
            timeframe === 'annual'
              ? 'bg-white dark:bg-background-card text-gray-900 dark:text-highlight-gold shadow-sm'
              : 'text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-text-primary'
          }`}
        >
          Annual
        </button>
      </div>
    </div>
  );

  // Stock Table now calculates category headers dynamically (like TickerTechnicals)
  // So we just pass the base category headers with original startIndices based on timeframe
  // Stock Table will calculate which headers to show based on current hiddenStatistics
  const allStatistics = timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_STATISTICS : FUNDAMENTAL_STATISTICS;
  const currentCategoryHeaders = timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_CATEGORY_HEADERS : FUNDAMENTAL_CATEGORY_HEADERS;

  // If public list is not found, show simple centered message
  // Also check publicUuid directly to ensure we exit this state when URL changes
  if (publicUuid && isPublicListNotFound) {
    return (
      <>
        <div className="flex flex-col min-h-screen">
          <Header />
          {!isAuthenticated && <PublicListPromoBar />}
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center">
              <p className="text-lg text-gray-700 dark:text-text-primary">
                {publicListError || 'This list does not exist or is no longer available.'}
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-8">
            <Footer />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <div className="flex flex-col min-h-screen">
      <StockTableLayout
        tickerList={displayTickerList}
        tickerData={displayTickerList.map(symbol => getTickerData(symbol)).filter(Boolean) as TickerDisplayData[]}
        loading={isLoading || isLoadingPublicList}
        error={error || (isPublicList ? publicListError : null)}
        lastUpdated={lastUpdated}
        isEditingTickers={isPublicList ? false : isEditingTickers}
        isPublicList={isPublicList}
        showPublicListPromoBar={isPublicList && !isAuthenticated}
        hiddenStatistics={hiddenStatistics}
        draggedTicker={draggedTicker}
        dragOverTicker={dragOverTicker}
        droppedTicker={droppedTicker}
        statistics={timeframe === 'annual' ? ANNUAL_FUNDAMENTAL_STATISTICS : FUNDAMENTAL_STATISTICS}
        categoryHeaders={currentCategoryHeaders}
        getTickerData={getTickerData}
        formatStatisticValue={formatStatisticValue}
        getStatisticColor={getStatisticColor}
        lockedStatistics={lockedStatistics}
        proOnlyStatistics={PRO_ONLY_STATISTICS}
        lockedTickers={lockedTickers}
        statisticsColumnWidthMultiplier={0.8}
        useListDailySmallScreenColumnWidth
        onRefetch={fetchFundamentalData}
        onTickerClick={handleTickerClick}
        onRemoveTicker={isPublicList ? () => {} : handleRemoveTicker}
        onDragStart={isPublicList ? () => {} : handleDragStart}
        onDragOver={isPublicList ? () => {} : handleDragOver}
        onDragLeave={isPublicList ? () => {} : handleDragLeave}
        onDrop={isPublicList ? () => {} : handleDrop}
        onDragEnd={isPublicList ? () => {} : handleDragEnd}
        onHideStatistic={isPublicList ? () => {} : handleHideStatistic}
        onShowStatistic={isPublicList ? () => {} : handleShowStatistic}
        onCategoryInfoClick={handleCategoryInfoClick}
        onIndicatorInfoClick={handleIndicatorInfoClick}
        listName={listName}
        onStartEdit={isPublicList ? () => {} : handleStartEdit}
        onCancelEdit={isPublicList ? () => {} : handleCancelEdit}
        onDoneEdit={isPublicList ? () => {} : handleDoneEdit}
        onAddTicker={isPublicList ? () => {} : handleAddTicker}
        onSave={handleSave}
        tempListName={tempListName}
        onTempListNameChange={setTempListName}
        userLists={isPublicList ? [] : userLists}
        currentListId={isPublicList ? undefined : (currentListId || undefined)}
        onListChange={isPublicList ? undefined : handleListChange}
        onCreateNewList={isPublicList ? undefined : handleCreateNewList}
        onCreateNewListWithName={isPublicList ? undefined : handleCreateNewListWithName}
        onDeleteList={handleDeleteList}
        showDeleteButton={!isPublicList && isAuthenticated && userLists.length > 1}
        onShareList={handleShareList}
        showShareButton={!isPublicList && isAuthenticated && currentListId !== null && !isCurrentListLocked}
        onRefreshUsageLimits={handleRefreshUsageLimits}
        showRefreshButton={false}
        showDelayedPricesText={false}
        showLastUpdatedDate={true}
        tabs={tabs}
        timeframeToggle={timeframeToggle}
      />
      
      {/* Footer */}
      <div className="mt-4 sm:mt-8">
        <Footer />
      </div>
    </div>
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
      
      {/* Indicator Info Modal */}
      <IndicatorInfoModal
        isOpen={isIndicatorModalOpen}
        onClose={handleCloseIndicatorModal}
        indicatorKey={selectedIndicatorKey}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteListConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        listName={listName}
      />
      
      {/* Error Dialog */}
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
      />
      
      {/* Share List Modal */}
      <ShareListModal
        isOpen={isShareModalOpen}
        onClose={handleShareModalClose}
        listId={currentListId}
        currentList={currentList}
        onUpdate={handleShareModalUpdate}
        isListLocked={isCurrentListLocked}
      />
    </>
  );
};

export default ListFundamentals;
